import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Sparkles, Magnet, ChevronDown } from "lucide-react";
import { WHY } from "../data/content";
import { Reveal, Overline } from "./Reveal";

const ICONS = [Clock, Sparkles, Magnet];

// Mobile detection — skip Framer Motion blur/scale animations on phones.
const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export default function WhyUs() {
  const [open, setOpen] = useState(null);

  return (
    <section data-testid="why-section" className="mx-auto max-w-7xl px-6 py-24 lg:py-28">
      <Reveal className="mb-12 text-center">
        <Overline>Pourquoi nous</Overline>
        <h2 className="font-head mx-auto mt-4 max-w-3xl text-4xl font-extrabold text-[#0B1220] sm:text-5xl">
          Elevate Influence est <span className="font-serif-i text-[#2563EB]">fait pour vous</span>.
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-3">
        {WHY.map((item, i) => {
          const Icon = ICONS[i];
          const isOpen = open === i;
          const dimmed = open !== null && !isOpen;
          return (
            <motion.div
              key={item.n}
              initial={IS_MOBILE ? false : { opacity: 0, y: 24 }}
              whileInView={IS_MOBILE ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={IS_MOBILE ? { duration: 0 } : { duration: 0.5, delay: i * 0.08 }}
              animate={IS_MOBILE ? false : {
                scale: isOpen ? 1.03 : dimmed ? 0.97 : 1,
                filter: dimmed ? "blur(3px)" : "blur(0px)",
                opacity: dimmed ? 0.45 : 1,
              }}
              data-testid={`why-${i}`}
              className={`flex h-full flex-col border border-[#E6EDF6] bg-white p-7 transition-shadow duration-300 ${
                isOpen ? "relative z-10 shadow-[0_24px_60px_-20px_rgba(37,99,235,0.35)]" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#2563EB]/10 text-[#2563EB]">
                  <Icon size={20} />
                </span>
                <span className="font-head text-3xl font-extrabold text-stroke">{item.n}</span>
              </div>

              <h3 className="font-head mt-6 text-lg font-bold leading-snug text-[#0B1220]">
                {item.q}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#5B6B85]">{item.body[0]}</p>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="more"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-4 border-t border-[#E6EDF6] pt-4 text-sm leading-[1.75] text-[#5B6B85]">
                      {item.body.slice(1).map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                data-testid={`why-toggle-${i}`}
                className="mt-auto inline-flex items-center gap-1.5 self-start pt-5 text-sm font-bold text-[#2563EB]"
              >
                {isOpen ? "Voir moins" : "Voir plus"}
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
